import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { HUDMeters } from './HUDMeters';
import { Button } from './ui/button';
import { 
  DynamicBreadcrumbs, 
  CoachBridgeCopy, 
  InstructionalFade,
  WhyToggle,
  TermTooltip,
} from './InstructionalComponents';
import { 
  BookOpen, 
  ChevronRight, 
  ChevronLeft,
  Lightbulb,
  MessageSquare,
  Target,
  Shield,
  AlertTriangle,
  Check,
  ArrowRight,
  Map,
  Sliders,
  User,
} from 'lucide-react';

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

// Breadcrumb items for navigation
const BREADCRUMB_ITEMS = [
  { id: 'orientation', label: 'Orientation' },
  { id: 'theory', label: 'Understand: Conversation Tools' },
  { id: 'prep', label: 'Plan: Conversation Plan' },
  { id: 'simulation', label: 'Rehearse: Simulation' },
];

// Bridge copy for transitions between modules
const BRIDGE_COPY = {
  2: "You've seen how stakeholder needs intersect. Now let's explore the approach that gives you the best chance of success.",
  5: "Trust and accountability can coexist — but the balance is delicate. The next module shows you how to hold both.",
  8: "You've seen the moves that build trust and hold accountability. Now, put them together into a plan you'll carry into the conversation.",
};

// Theory Reinforcement content for "Why" toggles
const WHY_CONTENT = {
  1: {
    title: 'The Stakeholder Web',
    content: 'Research from Harvard\'s Public Education Leadership Project shows that principals who explicitly map stakeholder needs before difficult conversations tend to resolve difficult conversations more effectively.',
  },
  2: {
    title: 'The Research on Inquiry',
    content: 'Edmondson\'s work on psychological safety demonstrates that leaders who ask questions before making statements create significantly more trust than those who lead with conclusions.',
  },
  3: {
    title: 'Emotional State Science',
    content: 'Neuroscience research shows that when someone feels judged, their prefrontal cortex (rational thinking) decreases in activity while their amygdala (fight-or-flight) increases.',
  },
};

// Theory Module Content
const MODULES = [
  {
    id: 1,
    title: 'Understanding the Stakes',
    subtitle: 'Module 01',
    content: `When a parent reaches out with a concern about their child's classroom experience, the situation involves multiple stakeholders with different needs:

**The Student** needs to feel safe and supported in the learning environment.

**The Teacher** needs to maintain professional dignity while being open to feedback.

**The Parent** needs assurance that their child's well-being is prioritized.

**You, as the administrator**, must balance these needs while maintaining trust with all parties.

The conversation with Ms. Ortega will shape not just this situation, but the ongoing professional relationship.

In this scenario, the quality of your first two moves will shape whether Ms. Ortega explains, defends, or shuts down.`,
    keyPoints: [
      'Multiple stakeholders have legitimate concerns',
      'Your approach sets the tone for resolution',
      'Professional relationships are built through difficult conversations',
    ],
    instruction: 'Review the stakeholder needs below. Consider how each perspective shapes the conversation.',
  },
  {
    id: 2,
    title: 'The Inquiry First Approach',
    subtitle: 'Module 02',
    content: `Leading with inquiry means seeking understanding before making judgments. This approach:

**Preserves Trust**: When teachers feel heard before being judged, they're more likely to engage openly.

**Gathers Context**: You may discover important details that change how you understand the situation.

**Models Fairness**: The same approach you want teachers to take with students, you should take with them.

Research on administrative conversations shows that inquiry first approaches more often lead to productive outcomes compared to judgment-first approaches.`,
    keyPoints: [
      'Seek to understand before evaluating',
      'Questions build trust; accusations erode it',
      'Context changes everything',
    ],
    previewChoices: [
      {
        id: 'inquiry',
        text: '"Before we decide what this requires, I want to hear how you experienced the moment."',
        impact: { trust: +10, clarity: +5, escalationRisk: -10 },
        label: 'Inquiry First',
        recommended: true,
      },
      {
        id: 'complaint',
        text: '"I need to talk to you about a parent complaint that raised concerns."',
        impact: { trust: -10, clarity: +5, escalationRisk: +10 },
        label: 'Lead with the Concern',
        recommended: false,
      },
    ],
    instruction: 'Preview how each opening changes the room before you choose one in the simulation.',
  },
  {
    id: 3,
    title: 'Managing Emotional States',
    subtitle: 'Module 03',
    content: `Ms. Ortega will enter this conversation in a **Guarded** state. She knows a concern was raised, expects judgment, and may feel defensive.

Your choices will shift her emotional state:

**Guarded → Reflective**: When she feels heard and respected, she'll engage more constructively.

**Guarded → Defensive**: If she feels attacked or blamed, she'll protect herself rather than problem-solve.

**Defensive → Escalated**: Continued pressure on a defensive person can lead to adversarial dynamics.

Watch for visual cues: body language, tone shifts, and direct statements about how the conversation is landing.`,
    keyPoints: [
      'Emotional states are dynamic, not fixed',
      'Your words shape her receptivity',
      'De-escalation requires intentional choices',
    ],
  },
  {
    id: 4,
    title: 'Naming vs. Blaming',
    subtitle: 'Module 04',
    content: `There's a crucial difference between **naming the issue** and **assigning blame**.

**Naming** sounds like: "The student felt embarrassed during the interaction."
**Blaming** sounds like: "You embarrassed a student."

Both acknowledge the same event, but they land very differently.

Naming opens dialogue. Blaming closes it.

When you name the student's experience without attributing intent or motive, you create space for the teacher to reflect and respond. When you blame, you trigger defense mechanisms.

In the simulation, the most effective naming moves keep the student's experience clear without assigning motive too early.`,
    keyPoints: [
      'Name the impact without assigning intent',
      'Leave room for the teacher\'s perspective',
      'Clarity and empathy can coexist',
    ],
  },
  {
    id: 5,
    title: 'The Accountability Balance',
    subtitle: 'Module 05',
    content: `Effective leadership requires holding two things simultaneously:

**Support**: "I'm here to help you navigate this."
**Accountability**: "We need to address what happened."

Too much support without accountability leads to vague outcomes and unresolved concerns.

Too much accountability without support damages trust and creates compliance without buy-in.

The goal is **clear accountability delivered with care**. This means being direct about expectations while honoring the teacher's professionalism and perspective.`,
    keyPoints: [
      'Support without accountability is enabling',
      'Accountability without support is punitive',
      'Aim for clarity delivered with care',
    ],
    hasLanguageSlider: true, // Enable the Impact Projection Slider
    instruction: 'Use the slider below to explore how different tones affect the leadership meters in real-time.',
  },
  {
    id: 6,
    title: 'Context and Equity',
    subtitle: 'Module 06',
    content: `Before drawing conclusions, consider what context might be relevant:

**Family History**: Has this family raised concerns before? Is there a pattern of sensitivity around certain topics?

**Student Context**: Are there factors in the student's life that might affect how they experienced the interaction?

**Teacher Intent vs. Impact**: What did the teacher intend? How did it land? These can be very different.

Applying an **equity lens** means asking whether context, power, prior history, or bias may be shaping how this moment was experienced — without jumping to conclusions before you have evidence.`,
    keyPoints: [
      'Context shapes how events are experienced',
      'Intent and impact often differ',
      'Consider systemic factors without assuming',
    ],
  },
  {
    id: 7,
    title: 'Building Toward Resolution',
    subtitle: 'Module 07',
    content: `A successful conversation ends with:

**Shared Understanding**: Both parties agree on what happened and why it matters.

**Clear Next Steps**: Specific, actionable commitments—not vague promises.

**Preserved Relationship**: The teacher feels respected, even if difficult feedback was given.

**Mutual Ownership**: Both parties have something to do, not just the teacher.

Avoid ending with "Just make sure it doesn't happen again." This creates compliance without learning. Instead, co-create a path forward that builds capacity.

A productive ending should leave both accountability and the relationship stronger than they were at the start.`,
    keyPoints: [
      'End with specific commitments, not vague assurances',
      'Preserve the professional relationship',
      'Create shared ownership of the path forward',
    ],
  },
  {
    id: 8,
    title: 'The Conversation Plan',
    subtitle: 'Module 08 - Action Required',
    content: `Before entering the simulation, you'll build your **Conversation Plan** — a strategic document that captures your intent.

This serves two purposes:

1. **Clarity for You**: Articulating your approach before the conversation helps you stay grounded.

2. **Self-Monitoring During the Conversation**: During the simulation, the system will compare your live choices against your stated intent. If your live choices drift from your stated approach, the coach will flag it.

Your plan will ask you to define:
- Your **Opening Stance** (Inquiry First, Lead with the Concern, etc.)
- What you **Need to Understand** before drawing conclusions
- What you **Must Not Avoid** addressing
- Whether you're keeping **Context and Equity** in view`,
    keyPoints: [
      'Articulate your strategy before you begin',
      'Intent mismatches trigger coaching feedback',
      'Your plan is your strategic anchor',
    ],
    isActionModule: true,
  },
  {
    id: 9,
    title: 'Ready for Simulation',
    subtitle: 'Module 09 - Final Review',
    content: `You've completed the theory modules. As you enter the simulation, keep four things in mind:

- Start by understanding before judging
- Name impact without overreaching
- Balance support with accountability
- End with a next step that is fair, clear, and workable

**Next Step**: If you haven't already, build your Conversation Plan. Then enter the simulation to practice these concepts with Ms. Ortega.

Remember: This is rehearsal. You can try different approaches and see how they land. Use this space to build your capacity.`,
    keyPoints: [
      'Theory prepares you; practice builds skill',
      'The simulation responds to your choices',
      'Learning happens through iteration',
    ],
    isFinalModule: true,
  },
];

export function LanguageLab() {
  const { state, setView, setPreviewMeters, clearPreview, visitModule } = useSimulation();
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [selectedPreviewChoice, setSelectedPreviewChoice] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showWhyPanel, setShowWhyPanel] = useState(false);
  const [sliderValue, setSliderValue] = useState(50); // For Language Lab slider
  const [sliderTimeout, setSliderTimeout] = useState(null);
  const [showSliderNudge, setShowSliderNudge] = useState(false);

  const currentModule = MODULES[currentModuleIndex];
  const isFirstModule = currentModuleIndex === 0;
  const isLastModule = currentModuleIndex === MODULES.length - 1;

  // Reset interaction state when module changes
  useEffect(() => {
    setHasInteracted(false);
    setShowWhyPanel(false);
    setSliderValue(50);
    setShowSliderNudge(false);
    if (sliderTimeout) clearTimeout(sliderTimeout);
    // Track that this module has been visited
    visitModule(currentModule.id);
  }, [currentModuleIndex, currentModule.id, visitModule]);

  // Slider nudge timeout - 15 seconds
  useEffect(() => {
    if (currentModule?.hasLanguageSlider && sliderValue !== 50 && !showSliderNudge) {
      const timeout = setTimeout(() => {
        setShowSliderNudge(true);
      }, 15000);
      setSliderTimeout(timeout);
      return () => clearTimeout(timeout);
    }
  }, [sliderValue, currentModule?.hasLanguageSlider, showSliderNudge]);

  // Calculate meter impacts based on slider position
  const calculateSliderImpact = (value) => {
    // 0 = Blunt/Directive, 100 = Soft/Supportive
    const normalized = (value - 50) / 50; // -1 to 1
    
    return {
      trust: Math.round(normalized * 15), // Soft increases trust
      efficacy: Math.round(normalized * -10), // Soft decreases efficacy  
      clarity: Math.round(normalized * -12), // Soft decreases clarity
      escalationRisk: Math.round(normalized * -10), // Soft decreases risk
    };
  };

  const handleSliderChange = (value) => {
    setSliderValue(value);
    setHasInteracted(true);
    setShowSliderNudge(false);
    setPreviewMeters(calculateSliderImpact(value));
  };

  const handlePrevious = () => {
    if (!isFirstModule) {
      setCurrentModuleIndex(currentModuleIndex - 1);
      setSelectedPreviewChoice(null);
      clearPreview();
    }
  };

  const handleNext = () => {
    if (!isLastModule) {
      setCurrentModuleIndex(currentModuleIndex + 1);
      setSelectedPreviewChoice(null);
      clearPreview();
    }
  };

  const handlePreviewChoice = (choice) => {
    setSelectedPreviewChoice(choice.id);
    setHasInteracted(true);
    setPreviewMeters(choice.impact);
  };

  const handleClearPreview = () => {
    setSelectedPreviewChoice(null);
    clearPreview();
  };

  const handleGoToPrepCard = () => {
    setView('prep');
  };

  // Get current breadcrumb index
  const getBreadcrumbIndex = () => {
    if (currentModuleIndex < 8) return 1; // Theory
    return 2; // Prep
  };

  // Icon map for content types
  const CONTENT_ICONS = {
    'The Student': User,
    'The Teacher': BookOpen,
    'The Parent': MessageSquare,
    'You, as the administrator': Target,
    'Preserves Trust': Shield,
    'Gathers Context': Lightbulb,
    'Models Fairness': Target,
    'Guarded → Reflective': Check,
    'Guarded → Defensive': AlertTriangle,
    'Defensive → Escalated': AlertTriangle,
    'Naming': Lightbulb,
    'Blaming': AlertTriangle,
    'Support': Shield,
    'Accountability': Target,
    'Family History': MessageSquare,
    'Student Context': Lightbulb,
    'Teacher Intent vs. Impact': Target,
    'Shared Understanding': Check,
    'Clear Next Steps': Target,
    'Preserved Relationship': Shield,
    'Mutual Ownership': Lightbulb,
  };

  // Color band palette cycling
  const BAND_COLORS = [
    'bg-[#002B45]/[0.03] border-l-[3px] border-[#0055A5]',
    'bg-[#0055A5]/[0.04] border-l-[3px] border-[#00A9E0]',
    'bg-[#78BE20]/[0.04] border-l-[3px] border-[#78BE20]',
    'bg-[#FFB81C]/[0.04] border-l-[3px] border-[#FFB81C]',
  ];

  // Parse markdown-like content with icons, colored bands, and term tooltips
  const renderContent = (content) => {
    let bandIndex = 0;

    return content.split('\n').map((paragraph, i) => {
      // Bold-only line → section header with icon
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        const text = paragraph.replace(/\*\*/g, '');
        const IconComp = CONTENT_ICONS[text];
        return (
          <div key={i} className="flex items-center gap-3 mb-3 mt-6 first:mt-0">
            {IconComp && (
              <span className="w-7 h-7 flex items-center justify-center bg-[#0055A5]/10 flex-shrink-0">
                <IconComp className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
              </span>
            )}
            <p className="text-sm font-semibold text-[#002B45] uppercase tracking-wide">
              {renderWithTerms(text)}
            </p>
          </div>
        );
      }

      // Lines with bold starts → colored band cards
      if (paragraph.includes('**')) {
        const parts = paragraph.split(/(\*\*[^*]+\*\*)/);
        const firstBold = parts.find(p => p.startsWith('**'));
        const headingText = firstBold ? firstBold.replace(/\*\*/g, '') : '';
        const IconComp = CONTENT_ICONS[headingText];
        const bandColor = BAND_COLORS[bandIndex % BAND_COLORS.length];
        bandIndex++;

        return (
          <div key={i} className={`${bandColor} p-4 mb-3`}>
            <div className="flex items-start gap-3">
              {IconComp && (
                <span className="w-6 h-6 flex items-center justify-center bg-white/80 flex-shrink-0 mt-0.5">
                  <IconComp className="w-3.5 h-3.5 text-[#0055A5]" strokeWidth={1.5} />
                </span>
              )}
              <p className="text-sm text-[#475569] leading-relaxed">
                {parts.map((part, j) =>
                  part.startsWith('**')
                    ? <strong key={j} className="text-[#002B45] font-semibold">{renderWithTerms(part.replace(/\*\*/g, ''))}</strong>
                    : renderWithTerms(part)
                )}
              </p>
            </div>
          </div>
        );
      }

      // Checkmark lines
      if (paragraph.startsWith('✓')) {
        return (
          <div key={i} className="flex items-start gap-3 py-2 px-4 mb-1 bg-[#78BE20]/[0.05]">
            <Check className="w-4 h-4 text-[#78BE20] mt-0.5 flex-shrink-0" strokeWidth={2} />
            <span className="text-sm text-[#475569] leading-relaxed">{renderWithTerms(paragraph.substring(2))}</span>
          </div>
        );
      }

      // Numbered list items (1., 2., etc.)
      if (/^\d+\./.test(paragraph.trim())) {
        const num = paragraph.trim().match(/^(\d+)\./)[1];
        const text = paragraph.trim().replace(/^\d+\.\s*/, '');
        return (
          <div key={i} className="flex items-start gap-3 py-2 mb-1">
            <span className="w-6 h-6 flex items-center justify-center bg-[#0055A5] text-white text-xs font-bold flex-shrink-0">
              {num}
            </span>
            <p className="text-sm text-[#475569] leading-relaxed">
              {text.includes('**')
                ? text.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                    part.startsWith('**')
                      ? <strong key={j} className="text-[#002B45] font-semibold">{renderWithTerms(part.replace(/\*\*/g, ''))}</strong>
                      : renderWithTerms(part)
                  )
                : renderWithTerms(text)
              }
            </p>
          </div>
        );
      }

      // Dash list items
      if (paragraph.trim().startsWith('- ')) {
        const text = paragraph.trim().substring(2);
        return (
          <div key={i} className="flex items-start gap-3 py-1.5 pl-4 mb-1">
            <span className="w-1.5 h-1.5 bg-[#0055A5] rounded-full mt-2 flex-shrink-0" />
            <p className="text-sm text-[#475569] leading-relaxed">
              {text.includes('**')
                ? text.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                    part.startsWith('**')
                      ? <strong key={j} className="text-[#002B45] font-semibold">{renderWithTerms(part.replace(/\*\*/g, ''))}</strong>
                      : renderWithTerms(part)
                  )
                : renderWithTerms(text)
              }
            </p>
          </div>
        );
      }

      if (paragraph.trim() === '') {
        return <div key={i} className="h-3" />;
      }

      // Default paragraph — lighter weight body text
      return (
        <p key={i} className="text-sm text-[#475569] mb-3 leading-relaxed">
          {renderWithTerms(paragraph)}
        </p>
      );
    });
  };

  // Wrap known glossary terms with TermTooltip
  const TOOLTIP_TERMS = [
    'psychological safety', 'inquiry-first', 'equity lens',
    'escalation risk', 'trust', 'efficacy', 'clarity',
    'compliance without buy-in', 'reassurance trap',
  ];

  const renderWithTerms = (text) => {
    if (typeof text !== 'string') return text;
    let segments = [text];

    TOOLTIP_TERMS.forEach(term => {
      const next = [];
      segments.forEach(segment => {
        if (typeof segment !== 'string') {
          next.push(segment);
          return;
        }
        const regex = new RegExp(`(${term})`, 'gi');
        const parts = segment.split(regex);
        parts.forEach((part, idx) => {
          if (part.toLowerCase() === term.toLowerCase()) {
            next.push(
              <TermTooltip key={`${term}-${idx}`} term={term}>
                {part}
              </TermTooltip>
            );
          } else if (part) {
            next.push(part);
          }
        });
      });
      segments = next;
    });

    return segments;
  };

  // Whether to show the HUD sidebar (only when module has interactive meter content)
  const showMeterSidebar = !!(currentModule.previewChoices || currentModule.hasLanguageSlider);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:grid lg:grid-cols-12" data-testid="language-lab">
      {/* Left Navigation */}
      <aside className="lg:col-span-3 bg-[#F1F5F9] p-4 lg:p-6">
        {/* Back to Hub */}
        <button
          onClick={() => setView('hub')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#002B45] mb-4 lg:mb-8 transition-colors"
          data-testid="back-to-hub"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Module List */}
        <div className="flex lg:flex-col lg:space-y-1 gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
          <p className="hidden lg:block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4 px-3">
            Conversation Tools
          </p>
          
          {MODULES.map((module, index) => {
            const isVisited = state.visitedModules.has(module.id);
            return (
            <button
              key={module.id}
              onClick={() => {
                setCurrentModuleIndex(index);
                setSelectedPreviewChoice(null);
                clearPreview();
              }}
              className={`w-full text-left px-3 py-2 transition-colors ${
                index === currentModuleIndex
                  ? 'bg-white text-[#002B45] shadow-sm'
                  : isVisited
                    ? 'text-[#78BE20]'
                    : 'text-slate-500 hover:text-[#002B45] hover:bg-white/50'
              }`}
              data-testid={`module-nav-${module.id}`}
            >
              <div className="flex items-center gap-2 lg:gap-3">
                <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  index === currentModuleIndex
                    ? 'bg-[#0055A5] text-white'
                    : isVisited
                      ? 'bg-[#78BE20] text-white'
                      : 'bg-slate-200 text-slate-500'
                }`}>
                  {isVisited && index !== currentModuleIndex ? '✓' : module.id}
                </span>
                <span className="text-xs lg:text-sm font-medium truncate hidden lg:inline">
                  {module.title}
                </span>
              </div>
            </button>
            );
          })}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${showMeterSidebar ? 'lg:col-span-6' : 'lg:col-span-9'} p-4 sm:p-6 lg:p-12`}>
        {/* Dynamic Breadcrumbs */}
        <DynamicBreadcrumbs 
          items={BREADCRUMB_ITEMS} 
          currentIndex={getBreadcrumbIndex()} 
        />

        {/* Bridge Copy (Coach Nudge Lead-in) */}
        {BRIDGE_COPY[currentModule.id] && (
          <CoachBridgeCopy message={BRIDGE_COPY[currentModule.id]} />
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentModule.id}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={stagger}
          >

        {/* Module Header with Why Toggle */}
        <motion.div variants={fadeUp} className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#0055A5]" strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
                Understand
              </span>
              <span className="text-xs text-slate-300">|</span>
              <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
                {currentModule.subtitle}
              </span>
            </div>
            
            {/* Why Toggle */}
            {WHY_CONTENT[currentModule.id] && (
              <WhyToggle
                title={WHY_CONTENT[currentModule.id].title}
                content={WHY_CONTENT[currentModule.id].content}
                isOpen={showWhyPanel}
                onToggle={() => setShowWhyPanel(!showWhyPanel)}
              />
            )}
          </div>
          
          <h1 className="text-3xl font-headline font-bold text-[#002B45] tracking-tight">
            {currentModule.title}
          </h1>
        </motion.div>

        {/* Instructional Fade */}
        {currentModule.instruction && (
          <InstructionalFade hasInteracted={hasInteracted}>
            {currentModule.instruction}
          </InstructionalFade>
        )}

        {/* Module Content */}
        <motion.div variants={fadeUp} className="prose prose-slate max-w-none mb-8" data-testid="module-content">
          {renderContent(currentModule.content)}
        </motion.div>

        {/* Impact Projection Slider (Module 5) */}
        {currentModule.hasLanguageSlider && (
          <div className="bg-white p-6 shadow-ambient mb-8" data-testid="impact-slider">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#F1F5F9] flex items-center justify-center">
                <Sliders className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
              </div>
              <span className="text-sm font-headline font-semibold text-[#002B45]">Impact Projection Slider</span>
            </div>
            
            <p className="text-sm text-[#475569] mb-6">
              Drag the slider to see how your tone affects the conversation dynamics.
            </p>
            
            {/* Slider Labels */}
            <div className="flex justify-between text-[10px] font-semibold uppercase tracking-widest mb-3">
              <span className="text-[#DA291C]">Over-directive</span>
              <span className="text-[#475569]">Balanced</span>
              <span className="text-[#78BE20]">Over-softened</span>
            </div>
            
            {/* Slider Track */}
            <div className="relative h-2 w-full bg-gradient-to-r from-[#DA291C] via-[#FFB81C] to-[#78BE20]">
              {/* Custom Slider */}
              <input
                type="range"
                min="0"
                max="100"
                value={sliderValue}
                onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="tone-slider"
              />
              {/* Slider Thumb */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#002B45] shadow-lg pointer-events-none"
                style={{ left: `calc(${sliderValue}% - 8px)` }}
              />
            </div>
            
            {/* Current Position Indicator */}
            <div className="mt-6 p-5 bg-[#F1F5F9]">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#475569] block mb-2">
                Sample Language
              </span>
              <p className="text-sm text-[#002B45] italic leading-relaxed">
                {sliderValue < 35 ? '"We need to address this issue immediately."' :
                 sliderValue > 65 ? '"I want to make sure you feel supported through this."' :
                 '"Let\'s work together to understand what happened."'}
              </p>
            </div>
            
            {/* Coach Nudge for Slider */}
            {showSliderNudge && (
              <div className="mt-5 p-5 bg-[#F1F5F9]">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#002B45] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">C</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[#0055A5] block mb-1">
                      Coach Observation
                    </span>
                    <p className="text-sm text-[#002B45]">
                      Notice how the Efficacy meter drops when you use overly soft language. 
                      Is that a trade-off you're willing to make?
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Key Points */}
        {currentModule.keyPoints && (
          <div className="bg-white p-6 shadow-ambient mb-8" data-testid="key-points">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-[#FFB81C]" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-[#002B45]">Key Takeaways</span>
            </div>
            
            <ul className="space-y-2">
              {currentModule.keyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#475569]">
                  <span className="text-[#0055A5] mt-1">•</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Choices (for Module 2) */}
        {currentModule.previewChoices && (
          <div className="bg-slate-50 p-6 mb-8" data-testid="preview-choices">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-[#0055A5]" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-[#002B45]">Preview Impact</span>
            </div>
            
            <p className="text-sm text-[#475569] mb-4">
              Hover over each option to see how it would affect your Leadership Instruments.
            </p>
            
            <div className="space-y-3">
              {currentModule.previewChoices.map((choice) => (
                <button
                  key={choice.id}
                  onMouseEnter={() => handlePreviewChoice(choice)}
                  onMouseLeave={handleClearPreview}
                  className={`w-full text-left p-4 transition-all ${
                    selectedPreviewChoice === choice.id
                      ? 'bg-white shadow-ambient'
                      : 'bg-white/50 hover:bg-white'
                  }`}
                  data-testid={`preview-choice-${choice.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                          {choice.label}
                        </span>
                        {choice.recommended && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#78BE20] bg-[#78BE20]/10 px-2 py-0.5">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#002B45] italic">
                        {choice.text}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action Module CTA */}
        {currentModule.isActionModule && (
          <div className="bg-[#0055A5] text-white p-6 shadow-ambient" data-testid="prep-card-cta">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-headline font-semibold mb-1">
                  Build Your Plan
                </h4>
                <p className="text-sm text-blue-100">
                  Clarify how you want to enter the conversation before you begin.
                </p>
              </div>
              
              <Button
                onClick={handleGoToPrepCard}
                className="bg-white text-[#0055A5] hover:bg-slate-100"
                data-testid="go-to-prep-card"
              >
                <Target className="w-4 h-4 mr-2" />
                Build Plan
              </Button>
            </div>
          </div>
        )}

        {/* Final Module CTA */}
        {currentModule.isFinalModule && (
          <motion.div variants={fadeUp} className="bg-[#002B45] text-white p-6 shadow-ambient" data-testid="final-cta">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-headline font-semibold mb-1">
                  Ready to Practice?
                </h4>
                <p className="text-sm text-slate-300">
                  Complete your plan and enter the simulation.
                </p>
              </div>
              
              <Button
                onClick={handleGoToPrepCard}
                className="bg-[#0055A5] text-white hover:bg-[#0055A5]/90"
                data-testid="final-prep-card"
              >
                Continue to Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-8 border-t border-slate-200 mt-8">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={isFirstModule}
            className={`${isFirstModule ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="prev-module"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <span className="text-sm text-slate-400">
            {currentModuleIndex + 1} of {MODULES.length}
          </span>
          
          <Button
            variant="ghost"
            onClick={handleNext}
            disabled={isLastModule}
            className={`${isLastModule ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="next-module"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>

      {/* Right Sidebar - HUD (only when module has interactive meter content) */}
      {showMeterSidebar && (
        <aside className="hidden lg:block lg:col-span-3 bg-white p-6 shadow-ambient">
          <div className="sticky top-6">
            <HUDMeters showPreview={selectedPreviewChoice !== null || sliderValue !== 50} />
            
            {/* Legend */}
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
                Meter Legend
              </p>
              
              <div className="space-y-3 text-xs text-[#475569]">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
                  <span><strong>Trust</strong>: Relationship strength</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
                  <span><strong>Efficacy</strong>: Professional competence</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
                  <span><strong>Clarity</strong>: Shared understanding</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#0055A5]" strokeWidth={1.5} />
                  <span><strong>Escalation Risk</strong>: Conflict likelihood</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}

export default LanguageLab;
